import { useQuery, useQueryClient } from '@tanstack/react-query'
import { labelService, Label, LabelResponse, LabelStats, LabelStatsResponse } from '../services/labelService'

/**
 * Hook for fetching all labels
 */
export const useLabels = (jql?: string) => {
  return useQuery<LabelResponse>({
    queryKey: ['labels', jql],
    queryFn: () => labelService.getAllLabels(jql),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook for searching labels
 */
export const useSearchLabels = (searchTerm: string, jql?: string, enabled: boolean = true) => {
  return useQuery<LabelResponse>({
    queryKey: ['labels', 'search', searchTerm, jql],
    queryFn: () => labelService.searchLabels(searchTerm, jql),
    enabled: enabled && !!searchTerm && searchTerm.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook for fetching label statistics
 */
export const useLabelStats = () => {
  return useQuery<LabelStatsResponse>({
    queryKey: ['labels', 'stats'],
    queryFn: () => labelService.getLabelStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook for fetching issues by label
 */
export const useIssuesByLabel = (label: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['labels', 'issues', label],
    queryFn: () => labelService.getIssuesByLabel(label),
    enabled: enabled && !!label,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook for fetching labels for a specific issue
 */
export const useIssueLabels = (issueKey: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['labels', 'issue', issueKey],
    queryFn: () => labelService.getIssueLabels(issueKey),
    enabled: enabled && !!issueKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

/**
 * Custom hook for managing labels with enhanced functionality
 */
export const useLabelsManager = () => {
  const queryClient = useQueryClient()

  const invalidateLabels = () => {
    queryClient.invalidateQueries({ queryKey: ['labels'] })
  }

  const clearCache = async () => {
    try {
      const result = await labelService.clearCache()
      if (result.success) {
        invalidateLabels()
      }
      return result
    } catch (error) {
      console.error('Error clearing labels cache:', error)
      return {
        success: false,
        message: 'Failed to clear labels cache',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  const prefetchLabels = (jql?: string) => {
    queryClient.prefetchQuery({
      queryKey: ['labels', jql],
      queryFn: () => labelService.getAllLabels(jql),
      staleTime: 5 * 60 * 1000,
    })
  }

  const prefetchLabelStats = () => {
    queryClient.prefetchQuery({
      queryKey: ['labels', 'stats'],
      queryFn: () => labelService.getLabelStats(),
      staleTime: 10 * 60 * 1000,
    })
  }

  return {
    invalidateLabels,
    clearCache,
    prefetchLabels,
    prefetchLabelStats,
  }
}

/**
 * Utility hook for getting enriched labels data
 */
export const useEnrichedLabels = (jql?: string) => {
  const labelsQuery = useLabels(jql)
  const statsQuery = useLabelStats()

  const enrichedLabels = labelsQuery.data?.data?.map(label => {
    const stats = statsQuery.data?.data?.mostUsedLabels?.find(stat => stat.label === label.name)
    return {
      ...label,
      isPopular: (stats?.count || 0) >= 5, // Consider labels with 5+ uses as popular
      rank: statsQuery.data?.data?.mostUsedLabels?.findIndex(stat => stat.label === label.name) + 1 || 0
    }
  }) || []

  return {
    ...labelsQuery,
    data: labelsQuery.data ? {
      ...labelsQuery.data,
      data: enrichedLabels
    } : undefined,
    enrichedLabels,
    isLoadingStats: statsQuery.isLoading,
    statsError: statsQuery.error,
  }
}
